/**
 * Team settings dialog (task #52).
 *
 * Team-wide settings surface, visible to ALL users (unlike the admin-only
 * SettingsModal). Currently hosts one section — the shared Keychain — but is
 * structured as collapsible sections so more team-wide settings can be added
 * later without reshaping this dialog.
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, KeyRound, Link2 } from "lucide-react";
import { KeychainSection } from "@/components/keychain-section";
import { TeamLinksSection } from "@/components/team-links-section";

interface TeamSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TeamSettingsDialog({ open, onOpenChange }: TeamSettingsDialogProps) {
  // All sections start collapsed — the user opens what they need.
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    links: false,
    keychain: false,
  });

  const toggle = (key: string) =>
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[92vw] max-h-[90vh] h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Team Share</DialogTitle>
          <DialogDescription>
            Gemeinsame Links und Zugänge für das ganze Team
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-2 pr-2">
          <Collapsible open={expandedSections.links} onOpenChange={() => toggle("links")}>
            <CollapsibleTrigger
              className="flex items-center gap-2 w-full px-2 py-2 rounded hover:bg-gray-700/50 text-left"
              data-testid="section-links"
            >
              {expandedSections.links ? (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-400" />
              )}
              <Link2 className="h-4 w-4 text-sky-400" />
              <span className="text-sm font-medium text-gray-200">Links</span>
              <span className="text-xs text-gray-500 ml-1">geteilte Team-Links</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="px-2 pt-1 pb-3">
              <TeamLinksSection />
            </CollapsibleContent>
          </Collapsible>

          <Collapsible open={expandedSections.keychain} onOpenChange={() => toggle("keychain")}>
            <CollapsibleTrigger
              className="flex items-center gap-2 w-full px-2 py-2 rounded hover:bg-gray-700/50 text-left"
              data-testid="section-keychain"
            >
              {expandedSections.keychain ? (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-400" />
              )}
              <KeyRound className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-medium text-gray-200">Keychain</span>
              <span className="text-xs text-gray-500 ml-1">shared team credentials</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="px-2 pt-1 pb-3">
              <KeychainSection />
            </CollapsibleContent>
          </Collapsible>
        </div>
      </DialogContent>
    </Dialog>
  );
}
